import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { TenantUrlHandler } from '../utils/tenantUrlHandler';

const UrlCustomization = ({ tenantId }) => {
  const [tenant, setTenant] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTenant();
  }, [tenantId]);

  const fetchTenant = async () => {
    try {
      let data;
      if (tenantId) {
        const result = await supabase
          .from('tenants')
          .select('*')
          .eq('id', tenantId)
          .single();
        data = result.data;
      } else {
        // Fallback to demo tenant for localhost
        const result = await supabase
          .from('tenants')
          .select('*')
          .eq('domain', 'demo')
          .single();
        data = result.data;
      }
      
      setTenant(data);
      setCustomDomain(data?.custom_domain || '');
    } catch (error) {
      console.error('Error fetching tenant:', error);
    }
  };

  const toggleSubdomain = async () => {
    setLoading(true);
    const { error } = await supabase
      .from('tenants')
      .update({ subdomain_enabled: !tenant.subdomain_enabled })
      .eq('id', tenant.id);

    if (!error) {
      setTenant(prev => ({ ...prev, subdomain_enabled: !prev.subdomain_enabled }));
    }
    setLoading(false);
  };

  const updateCustomDomain = async () => {
    setLoading(true);
    const { error } = await TenantUrlHandler.verifyCustomDomain(tenant.id, customDomain);
    
    if (!error) {
      await fetchTenant();
    }
    setLoading(false);
  };

  if (!tenant) return <div>Loading...</div>;

  const urls = TenantUrlHandler.generateUrls(tenant);

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4">URL Customization</h3>
      <p className="text-sm text-gray-600 mb-6">
        Configure how tenants access your property management portal.
      </p>
      
      {/* Subdomain Settings */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">Subdomain URL</label>
          <button
            onClick={toggleSubdomain}
            disabled={loading}
            className={`px-3 py-1 rounded text-sm ${
              tenant.subdomain_enabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {tenant.subdomain_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        {urls.subdomain && (
          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
            {urls.subdomain}
          </div>
        )}
      </div>

      {/* Custom Domain Settings */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Custom Domain</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            placeholder="your-domain.com"
            className="flex-1 px-3 py-2 border rounded-md"
          />
          <button
            onClick={updateCustomDomain}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Update
          </button>
        </div>
        {tenant.custom_domain && (
          <div className="mt-2">
            <div className={`text-sm px-2 py-1 rounded inline-block ${
              tenant.custom_domain_verified 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {tenant.custom_domain_verified ? 'Verified' : 'Pending Verification'}
            </div>
            {urls.customDomain && (
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-2">
                {urls.customDomain}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Setup Instructions */}
      <div className="space-y-4">
        {/* Subdomain Setup */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Subdomain Setup</h4>
          <p className="text-sm text-gray-600 mb-2">
            Subdomain URLs are automatically available once enabled.
          </p>
          <div className="text-xs text-gray-500">
            Format: <code>{tenant.domain}.rpms.verexone.com</code>
          </div>
        </div>

        {/* Custom Domain Setup */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Custom Domain Setup</h4>
          <div className="space-y-2 text-sm text-blue-800">
            <p><strong>Step 1:</strong> Enter your domain above</p>
            <p><strong>Step 2:</strong> Add DNS CNAME record:</p>
            <code className="bg-blue-100 px-2 py-1 rounded block">
              CNAME {customDomain || 'your-domain.com'} → rpms.verexone.com
            </code>
            <p><strong>Step 3:</strong> Wait for DNS propagation (up to 24 hours)</p>
            <p><strong>Step 4:</strong> Domain will be automatically verified</p>
          </div>
        </div>

        {/* Verification Status */}
        {tenant.custom_domain && (
          <div className={`p-4 rounded-lg ${
            tenant.custom_domain_verified 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                tenant.custom_domain_verified ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <h4 className={`font-medium ${
                tenant.custom_domain_verified ? 'text-green-900' : 'text-yellow-900'
              }`}>
                {tenant.custom_domain_verified ? 'Domain Verified' : 'Verification Pending'}
              </h4>
            </div>
            <p className={`text-sm mt-1 ${
              tenant.custom_domain_verified ? 'text-green-700' : 'text-yellow-700'
            }`}>
              {tenant.custom_domain_verified 
                ? 'Your custom domain is active and ready to use.' 
                : 'Checking DNS configuration. This may take up to 24 hours.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UrlCustomization;